import {ReactNode, useCallback, useEffect, useMemo, useRef} from 'react';

import {ProductSnippetAnalyticsContext} from 'modules/analytics/contexts/ProductSnippetAnalyticsContext';
import {
    IProductSnippetAnalytics,
    IProductStoreEventContext,
    TAddToCartPoint,
} from 'modules/analytics/types/ProductSnippetAnalytics';
import {buildProductStoreEventContext} from 'modules/analytics/helpers/buildProductStoreEventContext';

import {useGtagEventLocation} from 'hooks/analytics/gtag/helpers/useGtagEventLocation';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';

import {
    EventAction,
    EventCategory,
    EventCurrencyCodes,
    EventLabel,
    EventNames,
    EventPortalName,
} from 'modules/google-analytics/constants';

interface IProductSnippetAnalyticsProviderProps {
    children: ReactNode;
    enabled?: boolean;
}

interface IQueueItem {
    product: IProductSnippetAnalytics;
    storeEventContext: IProductStoreEventContext;
}

interface ITrackingParams {
    productAnalytics: IProductSnippetAnalytics;
    productUrl: string;
    category: {
        id?: number;
        name?: string;
    };
    storeEventContext: IProductStoreEventContext;
}

const BATCH_SIZE = 6;
const SEND_INTERVAL_MS = 2000;

const CURRENCY_CODE = EventCurrencyCodes.RUB;

// $.eventContext для «Клик на товар с переходом на PDP», «Добавление товара в корзину»
// и «Показ товара» (см. «Разметка. PLP. Сниппет»). Для показа контекст собирается позиционно —
// eventContext[i] соответствует products[i] в батче.
const buildStoreEventContext = (storeEventContext: IProductStoreEventContext, addToCartPoint?: TAddToCartPoint) => [
    buildProductStoreEventContext(storeEventContext, addToCartPoint),
];

// eslint-disable-next-line max-lines-per-function
export const ProductSnippetAnalyticsProvider = ({children, enabled = true}: IProductSnippetAnalyticsProviderProps) => {
    const {eventLocation} = useGtagEventLocation();

    const queueRef = useRef<IQueueItem[]>([]);
    const processedProductIdsRef = useRef(new Set<number>());
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const isSendingRef = useRef(false);

    const getListName = useCallback(
        (product: IProductSnippetAnalytics) => product.list || eventLocation,
        [eventLocation],
    );

    const sendAnalyticsEvent = useCallback(
        (payload: Record<string, unknown>) => {
            sendGtagEvent({
                event: EventNames.OWOX,
                eventLocation,
                ...payload,
            });
        },
        [eventLocation],
    );

    const createProductContext = useCallback(
        (product: IProductSnippetAnalytics, url?: string | null) =>
            `${getListName(product)} | ${product.id} | ${
                url ? `${window.location.origin}${url}` : window.location.href
            }`,
        [getListName],
    );

    const sendBatch = useCallback(
        (batch: IQueueItem[]) => {
            if (!batch.length) {
                return;
            }

            sendAnalyticsEvent({
                eventAction: EventAction.Show,
                eventCategory: EventCategory.NonInteractions,
                eventLabel: EventLabel.Products,
                eventPortalName: EventPortalName.Shop,
                eventContext: batch.map(({storeEventContext}) => buildProductStoreEventContext(storeEventContext)),
                ecommerce: {
                    currencyCode: CURRENCY_CODE,
                    impressions: {
                        actionField: {
                            list: getListName(batch[0].product),
                        },
                        products: batch.map(({product}) => product),
                    },
                },
            });
        },
        [getListName, sendAnalyticsEvent],
    );

    const flushQueue = useCallback(() => {
        if (isSendingRef.current || !queueRef.current.length) {
            return;
        }

        isSendingRef.current = true;

        try {
            while (queueRef.current.length) {
                sendBatch(queueRef.current.splice(0, BATCH_SIZE));
            }
        } finally {
            isSendingRef.current = false;
        }
    }, [sendBatch]);

    const enqueueProduct = useCallback(
        (product: IProductSnippetAnalytics, storeEventContext: IProductStoreEventContext) => {
            if (!enabled) {
                return;
            }

            if (processedProductIdsRef.current.has(product.id)) {
                return;
            }

            processedProductIdsRef.current.add(product.id);
            queueRef.current.push({product, storeEventContext});

            if (queueRef.current.length >= BATCH_SIZE) {
                flushQueue();
            }
        },
        [flushQueue, enabled],
    );

    useEffect(() => {
        intervalRef.current = setInterval(flushQueue, SEND_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            flushQueue();
        };
    }, [flushQueue]);

    const getTrackingHandlers = useCallback(
        ({productAnalytics, productUrl, category, storeEventContext}: ITrackingParams) => {
            const listName = getListName(productAnalytics);

            const getProductPayload = (quantityDelta?: number, cartQuantityAfter?: number) => ({
                ...productAnalytics,
                ...(undefined !== quantityDelta && {quantity: quantityDelta}),
                ...(undefined !== cartQuantityAfter && {
                    metric2: cartQuantityAfter,
                }),
            });

            return {
                onProductView: () => {
                    enqueueProduct(productAnalytics, storeEventContext);
                },

                onProductClick: () => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Interactions,
                        eventAction: EventAction.Click,
                        eventLabel: EventLabel.Product,
                        eventContext: buildStoreEventContext(storeEventContext),
                        ecommerce: {
                            currencyCode: CURRENCY_CODE,
                            click: {
                                actionField: {list: listName},
                                products: [productAnalytics],
                            },
                        },
                    });
                },

                onReviewsClick: () => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Interactions,
                        eventAction: EventAction.Click,
                        eventLabel: EventLabel.ProductReview,
                        eventContext: createProductContext(productAnalytics, `${productUrl}review`),
                    });
                },

                onStockClick: (stockUrl: string | null) => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Interactions,
                        eventAction: EventAction.Click,
                        eventLabel: EventLabel.ProductShield,
                        eventContext: createProductContext(productAnalytics, stockUrl),
                    });
                },

                onCatalogClick: (catalogUrl: string | null) => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Interactions,
                        eventAction: EventAction.Click,
                        eventLabel: EventLabel.ProductCatalog,
                        eventContext: createProductContext(productAnalytics, catalogUrl),
                    });
                },

                onAddToCartClick: (
                    quantityDelta: number,
                    cartQuantityAfter: number,
                    addToCartPoint: TAddToCartPoint = 'add_to_cart_button',
                ) => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Conversions,
                        eventAction: EventAction.Add,
                        eventLabel: `${EventLabel.Cart} | ${productAnalytics.name} | ${
                            productAnalytics.dimension3 || ''
                        }`,
                        eventContext: buildStoreEventContext(storeEventContext, addToCartPoint),
                        eventPortalName: EventPortalName.Shop,
                        eventCategoryId: category.id,
                        eventCategoryName: category.name,
                        eventPosition: productAnalytics.position,
                        eventProductId: productAnalytics.id,
                        eventProductName: productAnalytics.name,
                        eventProductPrice: productAnalytics.price,
                        eventProductRateLevel: productAnalytics.dimension13,
                        ecommerce: {
                            currencyCode: CURRENCY_CODE,
                            add: {
                                actionField: {list: listName},
                                products: [getProductPayload(quantityDelta, cartQuantityAfter)],
                            },
                        },
                    });
                },

                onRemoveFromCartClick: (quantityDelta: number, cartQuantityAfter: number) => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Interactions,
                        eventAction: EventAction.Remove,
                        eventLabel: `${EventLabel.Product} | ${EventAction.Remove} | ${productAnalytics.name}`,
                        eventContext: buildStoreEventContext(storeEventContext),
                        eventPortalName: EventPortalName.Shop,
                        eventCategoryId: category.id,
                        eventCategoryName: category.name,
                        eventPosition: productAnalytics.position,
                        eventProductId: productAnalytics.id,
                        eventProductName: productAnalytics.name,
                        eventProductPrice: productAnalytics.price,
                        eventProductRateLevel: productAnalytics.dimension13,
                        ecommerce: {
                            currencyCode: CURRENCY_CODE,
                            remove: {
                                actionField: {list: listName},
                                products: [getProductPayload(quantityDelta, cartQuantityAfter)],
                            },
                        },
                    });
                },

                onAddToWishlistClick: () => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Interactions,
                        eventAction: EventAction.Add,
                        eventLabel: EventLabel.Wishlist,
                        eventContext: createProductContext(productAnalytics),
                        ecommerce: {
                            currencyCode: CURRENCY_CODE,
                            click: {
                                list: listName,
                                products: [productAnalytics],
                            },
                        },
                    });
                },

                onRemoveFromWishlistClick: () => {
                    sendAnalyticsEvent({
                        eventCategory: EventCategory.Interactions,
                        eventAction: EventAction.Remove,
                        eventLabel: EventLabel.Wishlist,
                        eventContext: createProductContext(productAnalytics),
                        ecommerce: {
                            currencyCode: CURRENCY_CODE,
                            click: {
                                list: listName,
                                products: [productAnalytics],
                            },
                        },
                    });
                },
            };
        },
        [createProductContext, enqueueProduct, getListName, sendAnalyticsEvent],
    );

    const contextValue = useMemo(
        () => ({
            getTrackingHandlers,
        }),
        [getTrackingHandlers],
    );

    return (
        <ProductSnippetAnalyticsContext.Provider value={contextValue}>
            {children}
        </ProductSnippetAnalyticsContext.Provider>
    );
};
